import { useCallback, useEffect, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Search, X, PenLine,
  Ban, CheckCircle2, RefreshCw, ChevronLeft, ChevronRight, ArrowUpDown, Info, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { adminApi } from "../lib/adminAuth";
import {
  COLUMN_HELP, GLOSSARY_NOTE, GLOSSARY_SECTIONS, GSC_DISCONNECTED_HELP,
  actionHelp, actionLabel, sourceLabel, statusLabel,
} from "../lib/seoOpportunityLabels";

const TABS = [
  ["all", "All Opportunities"],
  ["medication_ingredient", "Medication × Ingredient"],
  ["customer_question", "Customer Questions"],
  ["routine", "Routines"],
  ["side_effect", "Side Effects"],
  ["product_guidance", "Product Guidance"],
  ["comparison", "Comparisons"],
  ["gsc_opportunity", "Search Console"],
  ["published", "Published"],
];
const CATEGORY_LABEL = Object.fromEntries(TABS);
const STATUS_STYLE = {
  discovered: "bg-stone-100 text-stone-600",
  selected: "bg-blue-50 text-blue-700",
  researching: "bg-indigo-50 text-indigo-700",
  writing: "bg-amber-50 text-amber-800",
  evidence_review: "bg-purple-50 text-purple-700",
  seo_review: "bg-purple-50 text-purple-700",
  ready_to_publish: "bg-teal-50 text-teal-700",
  published: "bg-emerald-50 text-emerald-800",
  needs_update: "bg-orange-50 text-orange-800",
  rejected: "bg-red-50 text-red-700",
  merged: "bg-stone-100 text-stone-500",
};
const STATUS_FILTER_OPTIONS = [
  "discovered", "selected", "researching", "writing", "evidence_review",
  "seo_review", "ready_to_publish", "published", "needs_update", "rejected",
];
const DASH = <span className="text-stone-400">—</span>;

function Counter({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-stone-200 bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">{label}</p>
      <p className="mt-1.5 text-2xl font-semibold text-stone-900">{value}</p>
    </div>
  );
}

function fmtNum(n) { return typeof n === "number" ? n.toLocaleString() : DASH; }
function fmtDate(iso) {
  if (!iso) return DASH;
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return DASH; }
}

// Info icon tooltip — opens on hover, keyboard focus and mobile tap.
function InfoTip({ text, label, testid }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <span
      className="relative inline-flex align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid={testid}
        aria-label={`More about ${label}`}
        aria-expanded={open}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-stone-400 transition-colors hover:text-stone-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      >
        <Info className="h-3 w-3" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-full z-30 mt-1.5 w-60 -translate-x-1/2 whitespace-normal rounded-lg border border-stone-200 bg-white p-2.5 text-left text-[11px] font-normal normal-case leading-snug tracking-normal text-stone-600 shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
}

// GSC "—" cell — explains why there is no data instead of showing a misleading 0.
function GscDash({ testid }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid={testid}
        aria-label={GSC_DISCONNECTED_HELP}
        aria-expanded={open}
        className="cursor-help text-stone-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      >
        —
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-full z-30 mt-1.5 w-52 -translate-x-1/2 whitespace-normal rounded-lg border border-stone-200 bg-white p-2.5 text-left text-[11px] leading-snug text-stone-600 shadow-lg"
        >
          {GSC_DISCONNECTED_HELP}
        </span>
      )}
    </span>
  );
}

function GlossaryDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="glossary-modal" className="max-h-[80vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>What do these columns mean?</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 text-sm">
          {GLOSSARY_SECTIONS.map((section) => (
            <div key={section.title}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">{section.title}</p>
              <dl className="mt-2 space-y-2.5">
                {section.items.map((item) => (
                  <div key={item.label}>
                    <dt className="font-semibold text-stone-900">{item.label}</dt>
                    <dd className="text-stone-600">{item.help}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
          <p data-testid="glossary-note" className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">
            {GLOSSARY_NOTE}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailDrawer({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    adminApi().get(`/admin/v2/seo-opportunities/opportunity/${id}`)
      .then(({ data }) => setD(data))
      .catch(() => { toast.error("Failed to load opportunity"); onClose(); });
  }, [id, onClose]);

  const act = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  const select = () => act(async () => {
    const { data } = await adminApi().post(`/admin/v2/seo-opportunities/opportunity/${id}/select`);
    toast.success("Blank article created — opening editor");
    nav(`/admin/blog?edit=${data.article_id}`);
  });
  const reject = () => act(async () => {
    const reason = window.prompt("Rejection reason?", "Not relevant to BlendSafe") || "";
    if (reason === "") return;
    await adminApi().post(`/admin/v2/seo-opportunities/opportunity/${id}/reject`, { reason });
    toast.success("Rejected"); onChanged(); onClose();
  });
  const markExisting = () => act(async () => {
    if (!window.confirm("Mark this as already covered by existing content?")) return;
    await adminApi().post(`/admin/v2/seo-opportunities/opportunity/${id}/mark-existing`);
    toast.success("Marked as existing"); onChanged(); onClose();
  });
  const refreshMetrics = () => act(async () => {
    const { data } = await adminApi().post(`/admin/v2/seo-opportunities/opportunity/${id}/refresh-metrics`);
    toast.message(data.message || "Metrics refresh requested");
  });

  const o = d?.opportunity;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="opportunity-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-stone-900">Opportunity details</h2>
          <button data-testid="drawer-close" onClick={onClose} className="text-stone-500 hover:text-stone-900"><X className="h-5 w-5" /></button>
        </div>
        {!o ? (
          <div className="mt-8 flex items-center gap-2 text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="mt-5 space-y-5 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-stone-500">Suggested content title</p>
              <h3 data-testid="drawer-title" className="mt-1 text-xl font-semibold text-stone-900">{o.recommended_title}</h3>
              <p className="mt-1 text-stone-500">{CATEGORY_LABEL[o.content_category] || o.content_category} · intent: {o.search_intent || "—"}</p>
            </div>

            <div data-testid="drawer-next-step" className="rounded-xl border border-stone-200 bg-stone-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Recommended next step</p>
              <p className="mt-1 font-semibold text-stone-900">{actionLabel(o.recommended_action)}</p>
              {actionHelp(o.recommended_action) && (
                <p className="mt-0.5 text-xs text-stone-600">{actionHelp(o.recommended_action)}</p>
              )}
              {o.select_enabled === false && (
                <p className="mt-1 text-xs font-medium text-amber-700">Selection is disabled until this is verified.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Customer's Google Search" value={o.primary_keyword} />
              <Field label="Monthly searches" value={fmtNum(o.monthly_searches)} />
              <Field label="Priority score (v2)" value={o.priority_score_v2 ?? o.opportunity_score} />
              <Field label="In our lane" value={o.in_our_lane ? `Yes · ${o.lane_fit_reason || ""}` : "No"} />
            </div>
            {o.difficulty_fetched_at && (
              <p className="text-[11px] text-stone-400">Difficulty updated: {new Date(o.difficulty_fetched_at).toLocaleDateString()}</p>
            )}

            <div className="rounded-xl border border-stone-200 bg-stone-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Search Console (not connected)</p>
              <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
                {["Clicks", "Impr.", "CTR", "Position"].map((l) => (
                  <div key={l}>
                    <div className="text-stone-500">{l}</div>
                    <div className="font-semibold text-stone-400" title={GSC_DISCONNECTED_HELP}>—</div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-stone-400">{GSC_DISCONNECTED_HELP}.</p>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Score breakdown (seo_priority_v2)</p>
              <div className="mt-2 space-y-1" data-testid="drawer-score-breakdown-v2">
                {Object.entries(o.score_components_v2 || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="capitalize text-stone-600">{k.replace(/_/g, " ")}</span><span className="font-semibold">{v}</span></div>
                ))}
                {o.score_incomplete && (
                  <div className="mt-1 text-[11px] text-amber-600">Winnability incomplete — difficulty not yet fetched.</div>
                )}
                {!o.score_components_v2 && Object.entries(o.score_components || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="capitalize text-stone-600">{k.replace(/_/g, " ")}</span><span className="font-semibold">{v}</span></div>
                ))}
              </div>
            </div>

            {o.supporting_keywords?.length > 0 && (
              <Field label="Supporting keywords" value={o.supporting_keywords.join(", ")} />
            )}
            <Field label="Suggested URL" value={o.suggested_url || o.existing_page_url || DASH} />
            {o.suggested_headings?.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Suggested headings</p>
                <ul className="mt-1 list-disc pl-5 text-stone-700">{o.suggested_headings.map((h) => <li key={h}>{h}</li>)}</ul>
              </div>
            )}
            <Field label="Recommended checker CTA" value={o.recommended_checker_cta || DASH} />

            <div className="rounded-xl border border-stone-200 bg-stone-50 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Source audit</p>
              <div className="mt-1.5 space-y-0.5 text-xs text-stone-600">
                <div>Data source: {sourceLabel(o.source_summary)}</div>
                <div>Raw source value: <span className="font-mono" data-testid="drawer-raw-source">{o.source_summary || "—"}</span></div>
                <div>Provider: {d.audit.keyword_provider || "—"}</div>
                <div>Country / lang: {d.audit.country || "—"} / {d.audit.language || "—"}</div>
                <div>Exact keyword: <span className="font-mono">{d.audit.exact_keyword || "—"}</span></div>
                <div>Broad-match volume: {d.audit.volume_is_broad_match ? "yes ⚠️" : "no"}</div>
                <div>Retrieved: {fmtDate(d.audit.provider_retrieved_at)}</div>
              </div>
            </div>

            <div className="sticky bottom-0 -mx-6 flex flex-wrap gap-2 border-t border-stone-200 bg-white px-6 py-4">
              <Button data-testid="drawer-select" onClick={select} disabled={busy || o.select_enabled === false}><PenLine className="h-4 w-4" /><span className="ml-1.5">Select for Article</span></Button>
              <Button data-testid="drawer-mark-existing" variant="outline" onClick={markExisting} disabled={busy}><CheckCircle2 className="h-4 w-4" /><span className="ml-1.5">Mark as Existing</span></Button>
              <Button data-testid="drawer-refresh" variant="outline" onClick={refreshMetrics} disabled={busy}><RefreshCw className="h-4 w-4" /><span className="ml-1.5">Refresh Metrics</span></Button>
              <Button data-testid="drawer-reject" variant="outline" onClick={reject} disabled={busy} className="text-red-600"><Ban className="h-4 w-4" /><span className="ml-1.5">Reject</span></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">{label}</p>
      <p className="mt-0.5 text-stone-800 break-words">{value ?? DASH}</p>
    </div>
  );
}

export default function AdminContentPlan() {
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counters, setCounters] = useState({});
  const [tabCounts, setTabCounts] = useState({});
  const [gscConnected, setGscConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [pageType, setPageType] = useState("");
  const [action, setAction] = useState("");
  const [sort, setSort] = useState("priority_score_v2");
  const [direction, setDirection] = useState("desc");
  const [lane, setLane] = useState(() => new URLSearchParams(window.location.search).get("lane") || "in_lane");
  const [laneCounts, setLaneCounts] = useState({ in_lane: 0, other: 0 });
  const [laneNote, setLaneNote] = useState("");
  const [freshness, setFreshness] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [openId, setOpenId] = useState(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tab, q, status, page_type: pageType, action, sort, direction, lane,
        page: String(page), page_size: String(pageSize),
      });
      const { data } = await adminApi().get(`/admin/v2/seo-opportunities/list?${params}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setCounters(data.counters || {});
      setTabCounts(data.tab_counts || {});
      setGscConnected(!!data.gsc_connected);
      setLaneCounts(data.lane_counts || { in_lane: 0, other: 0 });
      setLaneNote(data.lane_explanation || "");
      // Reflect lane in the URL for shareable state.
      const url = new URL(window.location.href);
      url.searchParams.set("lane", lane);
      window.history.replaceState({}, "", url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load opportunities");
    } finally { setLoading(false); }
  }, [tab, q, status, pageType, action, sort, direction, lane, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    adminApi().get(`/admin/v2/seo-opportunities/metrics/freshness`)
      .then(({ data }) => setFreshness(data)).catch(() => {});
  }, []);
  useEffect(() => { setPage(1); }, [tab, q, status, pageType, action, lane]);

  const recomputeScores = async () => {
    setRecomputing(true);
    try {
      const { data } = await adminApi().post(`/admin/v2/seo-opportunities/recompute-scores`);
      const lc = data.lane_counts || {};
      toast.success(`Recomputed ${data.recomputed} scores · ${lc.in_lane ?? 0} in lane, ${lc.out_of_lane ?? 0} other`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally { setRecomputing(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const toggleSort = (field) => {
    if (sort === field) setDirection((d) => (d === "desc" ? "asc" : "desc"));
    else { setSort(field); setDirection("desc"); }
  };
  const SortH = ({ field, col, className = "" }) => (
    <th className={`px-3 py-3 font-semibold ${className}`}>
      <span className="inline-flex items-center">
        <button className="inline-flex items-center gap-1 hover:text-stone-900" onClick={() => toggleSort(field)} data-testid={`sort-${field}`}>
          {COLUMN_HELP[col].label} <ArrowUpDown className="h-3 w-3 opacity-50" />
        </button>
        <InfoTip text={COLUMN_HELP[col].help} label={COLUMN_HELP[col].label} testid={`help-${col}`} />
      </span>
    </th>
  );
  const PlainH = ({ col }) => (
    <th className="px-3 py-3 font-semibold">
      <span className="inline-flex items-center">
        {COLUMN_HELP[col].label}
        <InfoTip text={COLUMN_HELP[col].help} label={COLUMN_HELP[col].label} testid={`help-${col}`} />
      </span>
    </th>
  );

  return (
    <main data-testid="admin-content-plan-page" className="min-h-screen bg-[#fdf8f3] px-6 py-10">
      <div className="mx-auto max-w-7xl">
        <AdminHeader />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">SEO Content Opportunities</h1>
            <p className="mt-1 max-w-3xl text-sm text-stone-600">
              Medication interactions, customer questions and Search Console opportunities ranked by demand and relevance. Review and select articles to write.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="recompute-scores-btn"
              onClick={recomputeScores}
              disabled={recomputing}
              variant="outline"
              className="h-9"
            >
              {recomputing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1.5">Recompute scores</span>
            </Button>
            <Link
              to="/admin/maintenance"
              data-testid="link-maintenance"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 text-sm text-stone-700 hover:bg-stone-50"
            >
              <Wrench className="h-4 w-4" /> Maintenance
            </Link>
            <span data-testid="gsc-badge" className={`rounded-full px-3 py-1 text-xs font-semibold ${gscConnected ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
              Search Console: {gscConnected ? "Connected" : "Disconnected"}
            </span>
          </div>
        </div>

        <section className="mt-6 grid gap-3 sm:grid-cols-5">
          <Counter testid="counter-discovered" label="Discovered" value={counters.discovered ?? 0} />
          <Counter testid="counter-selected" label="Selected" value={counters.selected ?? 0} />
          <Counter testid="counter-writing" label="Writing" value={counters.writing ?? 0} />
          <Counter testid="counter-review" label="Review" value={counters.review ?? 0} />
          <Counter testid="counter-published" label="Published" value={counters.published ?? 0} />
        </section>

        {freshness && (
          <div data-testid="freshness-banner" className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600">
            <span>Difficulty data updated:{" "}
              <span className={freshness.difficulty_stale ? "font-semibold text-amber-600" : "text-stone-900"}>
                {freshness.difficulty_updated ? new Date(freshness.difficulty_updated).toLocaleDateString() : "never"}{freshness.difficulty_stale ? " · Stale" : ""}
              </span></span>
            <span>Search volume updated:{" "}
              <span className={freshness.search_volume_stale ? "font-semibold text-amber-600" : "text-stone-900"}>
                {freshness.search_volume_updated ? new Date(freshness.search_volume_updated).toLocaleDateString() : "never"}{freshness.search_volume_stale ? " · Stale" : ""}
              </span></span>
            <span>Search Console data updated:{" "}
              <span className="text-stone-900">{freshness.gsc?.last_success ? new Date(freshness.gsc.last_success).toLocaleDateString() : "—"}</span></span>
          </div>
        )}

        <section className="mt-4 flex flex-wrap items-center gap-2">
          <button data-testid="lane-in" onClick={() => setLane("in_lane")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${lane === "in_lane" ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
            In Our Lane ({laneCounts.in_lane})
          </button>
          <button data-testid="lane-other" onClick={() => setLane("other")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${lane === "other" ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
            Explore Other Topics ({laneCounts.other})
          </button>
          <button data-testid="lane-all" onClick={() => setLane("all")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${lane === "all" ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
            All
          </button>
          {laneNote && <span className="text-xs text-stone-500 max-w-xl">{laneNote}</span>}
        </section>

        <div className="mt-6 flex flex-wrap gap-1.5 border-b border-stone-200">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`rounded-t-lg px-3 py-2 text-[13px] font-medium transition-colors ${tab === key ? "border-b-2 border-stone-900 text-stone-900" : "text-stone-500 hover:text-stone-800"}`}
            >
              {label} <span className="text-stone-400">({tabCounts[key] ?? 0})</span>
            </button>
          ))}
        </div>

        <section className="mt-4 flex flex-wrap items-center gap-2.5">
          <div className="flex h-10 items-center gap-2 rounded-lg border border-stone-300 bg-white px-3">
            <Search className="h-4 w-4 text-stone-400" />
            <input data-testid="opp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or keyword…" className="w-60 bg-transparent text-sm focus:outline-none" />
          </div>
          <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-lg border border-stone-300 bg-white px-3 text-sm">
            <option value="">All statuses</option>
            {STATUS_FILTER_OPTIONS.map((s) => (
              <option key={s} value={s}>{statusLabel(s)}</option>
            ))}
          </select>
          <select data-testid="filter-pagetype" value={pageType} onChange={(e) => setPageType(e.target.value)} className="h-10 rounded-lg border border-stone-300 bg-white px-3 text-sm">
            <option value="">Existing & new</option>
            <option value="existing">Existing page</option>
            <option value="new">New page</option>
          </select>
          <select data-testid="filter-action" value={action} onChange={(e) => setAction(e.target.value)} className="h-10 rounded-lg border border-stone-300 bg-white px-3 text-sm">
            <option value="">All actions</option>
            <option value="CREATE_ARTICLE">{actionLabel("CREATE_ARTICLE")}</option>
            <option value="CREATE_HUB">{actionLabel("CREATE_HUB")}</option>
            <option value="IMPROVE_EXISTING">{actionLabel("IMPROVE_EXISTING")}</option>
            <option value="NEEDS_MAPPING">{actionLabel("NEEDS_MAPPING")}</option>
            <option value="REJECT">{actionLabel("REJECT")}</option>
          </select>
          <span className="text-xs text-stone-500">{total} results</span>
          <button
            data-testid="glossary-link"
            onClick={() => setGlossaryOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 underline decoration-stone-300 underline-offset-2 transition-colors hover:text-stone-900"
          >
            <Info className="h-3.5 w-3.5" /> What do these columns mean?
          </button>
        </section>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <section className="mt-4 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="opportunities-table">
                <thead className="border-b border-stone-200 bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                  <tr>
                    <PlainH col="title" />
                    <PlainH col="keyword" />
                    <PlainH col="category" />
                    <SortH field="monthly_searches" col="monthly" className="text-right" />
                    <SortH field="trend" col="trend" />
                    <SortH field="gsc_impressions" col="gscImpressions" />
                    <PlainH col="gscPosition" />
                    <PlainH col="source" />
                    <SortH field="priority_score_v2" col="score" className="text-right" />
                    <PlainH col="action" />
                    <th className="px-3 py-3 font-semibold">Status</th>
                    <SortHDate />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.length === 0 ? (
                    <tr><td colSpan={12} className="px-3 py-10 text-center text-stone-400">No opportunities in this view yet.</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.opportunity_id} data-testid={`opp-row-${r.opportunity_id}`} onClick={() => setOpenId(r.opportunity_id)} className="cursor-pointer hover:bg-stone-50/60">
                      <td className="px-3 py-3 font-medium text-stone-900 max-w-xs truncate">{r.recommended_title}</td>
                      <td className="px-3 py-3 text-stone-600 max-w-[180px] truncate">{r.primary_keyword}</td>
                      <td className="px-3 py-3 text-stone-500">{CATEGORY_LABEL[r.content_category] || r.content_category}</td>
                      <td className="px-3 py-3 text-right font-semibold text-stone-900">{fmtNum(r.monthly_searches)}</td>
                      <td className="px-3 py-3">{r.trend ?? DASH}</td>
                      <td className="px-3 py-3" data-testid={`gsc-impr-cell-${r.opportunity_id}`}>
                        {r.gsc_impressions == null ? <GscDash testid={`gsc-impr-na-${r.opportunity_id}`} /> : fmtNum(r.gsc_impressions)}
                      </td>
                      <td className="px-3 py-3" data-testid={`gsc-pos-cell-${r.opportunity_id}`}>
                        {r.gsc_position == null ? <GscDash testid={`gsc-pos-na-${r.opportunity_id}`} /> : r.gsc_position}
                      </td>
                      <td className="px-3 py-3 text-stone-500 text-xs">{sourceLabel(r.source_summary)}</td>
                      <td className="px-3 py-3 text-right"><span className="rounded-full bg-stone-900/90 px-2 py-0.5 text-xs font-bold text-white">{r.priority_score_v2 ?? r.opportunity_score}</span></td>
                      <td className="px-3 py-3 text-stone-600 text-xs">
                        <span className="inline-flex items-center">
                          {actionLabel(r.recommended_action)}
                          <InfoTip text={actionHelp(r.recommended_action)} label={actionLabel(r.recommended_action)} testid={`action-help-${r.opportunity_id}`} />
                        </span>
                      </td>
                      <td className="px-3 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_STYLE[r.status] || "bg-stone-100 text-stone-600"}`}>{statusLabel(r.status)}</span></td>
                      <td className="px-3 py-3 text-stone-500">{fmtDate(r.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-stone-200 px-4 py-3 text-sm">
              <span className="text-stone-500">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button data-testid="page-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Button data-testid="page-next" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </section>
        )}
      </div>

      <GlossaryDialog open={glossaryOpen} onOpenChange={setGlossaryOpen} />
      {openId && <DetailDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </main>
  );

  function SortHDate() {
    return (
      <th className="px-3 py-3 font-semibold">
        <button className="inline-flex items-center gap-1 hover:text-stone-900" onClick={() => toggleSort("updated_at")} data-testid="sort-updated_at">
          Updated <ArrowUpDown className="h-3 w-3 opacity-50" />
        </button>
      </th>
    );
  }
}
